/**
 * 売り方の提案。商品知識の属性から、切り口ごとに「今どう打ち出すか」の案を出す。
 *
 * ## 案が出せないことを 0 件と描かない
 *
 * 値が入っていない切り口は、案の欄に「—」だけを置かず、返ってきた理由
 * （`dataGap`）をそのまま出す。どの切り口にも案が出せないときは、表の前に
 * 「いま出せる提案はありません」と理由をまとめて出す。
 *
 * ## 採否は記録するだけ
 *
 * 採用しても外部へ書き戻さない。案が出ていない切り口は採用できない
 * （サーバーが断る）ので、ボタンも出さない。
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  SellingAnglesDocument,
  SellingProductChoicesDocument,
  SetSellingAngleAdoptionDocument,
  Tag,
  type SellingAngleFieldsFragment,
  type SellingProductChoicesQuery,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  SELLING_ADOPTION_IS_RECORD_ONLY_NOTE,
  SELLING_IDEA_IS_NOT_GENERATED_NOTE,
  SELLING_METRICS_NOT_CONNECTED_NOTE,
} from "../api-capabilities";

import { ProductSearchField, ProductLoadMore } from "./ProductSearchControls";
import {
  AnswerableFilterFields,
  toAnswerableFilter,
  type AnswerableChoice,
} from "./AnswerableFilterFields";

type ProductChoice =
  SellingProductChoicesQuery["products"]["edges"][number]["node"];

interface PageUserError {
  readonly code: string;
  readonly message: string;
}

/** 出所の札。値が無いときは「未取得」と出す（0 件と読ませない）。 */
const SOURCE_LABELS: Record<string, string> = {
  MANUAL: "人が確定",
  INFERRED: "機械生成",
  MASTER: "商品マスター",
  NONE: "未取得",
};

export function SellingAnglesPage() {
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<ProductChoice[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductChoice | null>(
    null,
  );
  // 既定は従来どおり「絞らない」。答えられる商品に絞ると、集計していない
  // 商品は候補に出なくなる
  const [answerable, setAnswerable] = useState<AnswerableChoice>("");
  const [answerablePersona, setAnswerablePersona] = useState("");
  const answerableFilter = toAnswerableFilter(answerable, answerablePersona);
  const [choices] = useQuery({
    query: SellingProductChoicesDocument,
    variables: {
      first: 20,
      after: cursor,
      filter: {
        sellingCandidates: true,
        ...(submittedQuery === "" ? {} : { query: submittedQuery }),
        ...(answerableFilter === null ? {} : { answerable: answerableFilter }),
      },
    },
  });
  const products = [
    ...new Map(
      [
        ...loaded,
        ...(choices.data?.products.edges.map((edge) => edge.node) ?? []),
      ].map((product) => [product.id, product]),
    ).values(),
  ];
  const firstProduct = products[0];
  const selected = selectedProduct?.id ?? firstProduct?.id ?? null;
  const outsideSelection =
    selectedProduct !== null &&
    !products.some((product) => product.id === selectedProduct.id);

  useEffect(() => {
    if (selectedProduct === null && firstProduct !== undefined)
      setSelectedProduct(firstProduct);
  }, [firstProduct, selectedProduct]);

  if (choices.fetching && choices.data === undefined) {
    return <LoadingState />;
  }
  if (choices.error !== undefined || choices.data === undefined) {
    return <ErrorState description={choices.error?.message} />;
  }

  return (
    <div className="vd-page">
      <PageHeader
        title="売り方の提案"
        description="商品に入っている属性から、いまどう打ち出せるかの案を出します。属性が入っていない切り口では案を出しません。"
        titleExtra={<HelpIcon slug="knowledge/selling" />}
      />

      <div className="vd-stack">
        <Card
          title="対象の商品を探す"
          extra={
            <HelpIcon slug="knowledge/selling" anchor="対象の商品を探す" />
          }
        >
          <form
            className="vd-row"
            onSubmit={(event) => {
              event.preventDefault();
              setSubmittedQuery(query.trim());
              setCursor(null);
              setLoaded([]);
            }}
          >
            <ProductSearchField value={query} onChange={setQuery} />
            <Button type="submit" disabled={choices.fetching}>
              検索
            </Button>
          </form>
          <div style={{ marginTop: 12 }}>
            <AnswerableFilterFields
              idPrefix="selling"
              choice={answerable}
              personaId={answerablePersona}
              onChange={(nextChoice, nextPersona) => {
                setAnswerable(nextChoice);
                setAnswerablePersona(nextPersona);
                setCursor(null);
                setLoaded([]);
              }}
            />
          </div>
          <div className="vd-field" style={{ maxWidth: 360, marginTop: 12 }}>
            <label htmlFor="selling-product">対象の商品</label>
            <select
              id="selling-product"
              className="vd-input"
              value={selected ?? ""}
              onChange={(event) => {
                const product = products.find(
                  (candidate) => candidate.id === event.target.value,
                );
                if (product !== undefined) setSelectedProduct(product);
              }}
            >
              {outsideSelection ? (
                <option value={selectedProduct.id}>
                  {selectedProduct.name}（選択中）
                </option>
              ) : null}
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
            <p className="vd-stat-note">
              候補は、属性の機械生成の対象になれる商品（UGC 50 件以上の上位 200
              商品）と、属性が生成済みの商品です。UGC の無い商品は出ません。
            </p>
            {!choices.fetching && products.length === 0 ? (
              <p role="status">該当する商品がありません</p>
            ) : null}
          </div>
          <ProductLoadMore
            hasNextPage={choices.data.products.pageInfo.hasNextPage}
            fetching={choices.fetching}
            label="続きを読む"
            onLoad={() => {
              const endCursor = choices.data?.products.pageInfo.endCursor;
              if (endCursor == null) return;
              setLoaded(products);
              setCursor(endCursor);
            }}
          />
        </Card>

        {selected === null ? (
          <Card
            title="属性から導いた訴求案"
            extra={
              <HelpIcon
                slug="knowledge/selling"
                anchor="属性から導いた訴求案"
              />
            }
          >
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              商品がまだありません。
            </p>
          </Card>
        ) : (
          <SellingAnglesCard productId={selected} />
        )}

        <Card
          title="指標との突き合わせ"
          extra={
            <HelpIcon slug="knowledge/selling" anchor="指標との突き合わせ" />
          }
          note={SELLING_METRICS_NOT_CONNECTED_NOTE}
        >
          <p style={{ margin: 0 }}>
            この画面では、採否と指標を並べて置いていません。つないでいない数字の
            枠だけを出すと、数えた結果の 0 と見分けられなくなるためです。
          </p>
        </Card>
      </div>
    </div>
  );
}

function SellingAnglesCard({ productId }: { productId: string }) {
  const [result, refetch] = useQuery({
    query: SellingAnglesDocument,
    variables: { productId },
    // **商品知識シートで値を確定してからここへ来ると、キャッシュが古い。**
    // 案の材料は別の面（`KnowledgeSheet`）で書き換わるので、この問い合わせの
    // 型を返す mutation が無く、document cache が古い結果を無効にできない
    // （dev の通しで実際に「値が入っていません」のまま出た）。
    // 開き直すたびに読み直し、描けているものは出したままにする
    requestPolicy: "cache-and-network",
  });
  const [mutationState, setAdoption] = useMutation(
    SetSellingAngleAdoptionDocument,
  );
  const [userErrors, setUserErrors] = useState<PageUserError[]>([]);

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const angles = result.data.sellingAngles;
  const usable = angles.filter((angle) => angle.idea !== null);
  const adopted = angles.filter(
    (angle) => angle.idea !== null && angle.adoption === "ADOPTED",
  );
  const busy = mutationState.fetching;

  const toggle = (angle: SellingAngleFieldsFragment): void => {
    void setAdoption({
      input: {
        productId,
        angleKey: angle.key,
        adoption: angle.adoption === "ADOPTED" ? "ON_HOLD" : "ADOPTED",
      },
    }).then((response) => {
      if (response.error !== undefined) {
        setUserErrors([
          {
            code: "INTERNAL_ERROR",
            message: `保存できませんでした: ${String(response.error)}`,
          },
        ]);
        return;
      }
      const payload = response.data?.setSellingAngleAdoption;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({
            code,
            message,
          })),
        );
        return;
      }
      setUserErrors([]);
      refetch({ requestPolicy: "network-only" });
    });
  };

  return (
    <div className="vd-stack">
      <Card
        title="属性から導いた訴求案"
        extra={
          <HelpIcon slug="knowledge/selling" anchor="属性から導いた訴求案" />
        }
        note={SELLING_IDEA_IS_NOT_GENERATED_NOTE}
      >
        {userErrors.length === 0 ? null : (
          <div className="vd-notice vd-notice--error" role="alert">
            {userErrors.map((error) => (
              <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))}
          </div>
        )}

        {usable.length === 0 ? (
          <div className="vd-notice" role="status">
            <strong>いま出せる提案はありません。</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: "1.2em" }}>
              {angles.map((angle) => (
                <li key={angle.key}>
                  {angle.cut}: {angle.dataGap?.why ?? "案を出せませんでした。"}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="vd-table-scroll">
          <table className="vd-table">
            <thead>
              <tr>
                <th>切り口</th>
                <th>使う属性</th>
                <th>案</th>
                <th>採否</th>
              </tr>
            </thead>
            <tbody>
              {angles.map((angle) => (
                <tr key={angle.key}>
                  <th scope="row">{angle.cut}</th>
                  <td>
                    {angle.attributes.length === 0 ? (
                      <Tag>未取得</Tag>
                    ) : (
                      <>
                        {angle.attributes.join(" / ")}{" "}
                        <Tag tone="info">
                          {SOURCE_LABELS[angle.attributeSource] ?? "未取得"}
                        </Tag>
                      </>
                    )}
                  </td>
                  <td aria-label={`${angle.cut}の案`}>
                    {angle.idea ??
                      angle.dataGap?.why ??
                      "案を出せませんでした。"}
                  </td>
                  <td>
                    {angle.idea === null ? (
                      <span className="vd-stat-note">案がありません</span>
                    ) : (
                      // **いまの状態（札）と、押したら起きること（ボタン）を分ける。**
                      // 1 つのボタンに状態を書くと、「採用」が
                      // 「いま採用されている」なのか「押すと採用になる」なのか
                      // 読み取れない
                      <div className="vd-row">
                        <Tag
                          tone={angle.adoption === "ADOPTED" ? "ok" : "neutral"}
                        >
                          {angle.adoption === "ADOPTED" ? "採用中" : "保留中"}
                        </Tag>
                        <Button
                          disabled={busy}
                          aria-label={
                            angle.adoption === "ADOPTED"
                              ? `${angle.cut}の案を保留に戻す`
                              : `${angle.cut}の案を採用する`
                          }
                          onClick={() => toggle(angle)}
                        >
                          {angle.adoption === "ADOPTED"
                            ? "保留に戻す"
                            : "採用する"}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="採用した切り口"
        note={SELLING_ADOPTION_IS_RECORD_ONLY_NOTE}
        extra={<HelpIcon slug="knowledge/selling" anchor="採用した切り口" />}
      >
        {adopted.length === 0 ? (
          <p className="vd-card-note" style={{ marginTop: 0 }}>
            採用した案はまだありません。
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
            {adopted.map((angle) => (
              <li key={angle.key}>
                {angle.cut}: {angle.idea}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
